import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import './CompanyRegistration.css';

const CompanyRegistration = () => {
    const history = useHistory();
    const [formData, setFormData] = useState({
        companyName: '',
        subdomain: '',
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        phone: ''
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Auto-generate subdomain from company name
        if (name === 'companyName') {
            const subdomain = value.toLowerCase()
                .replace(/[^a-z0-9]/g, '-')
                .replace(/-+/g, '-')
                .replace(/^-|-$/g, '');
            setFormData(prev => ({ ...prev, subdomain }));
        }

        // Clear field error on change
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.companyName) newErrors.companyName = 'Company name is required';
        if (!formData.subdomain) newErrors.subdomain = 'Subdomain is required';
        if (!/^[a-z0-9-]+$/.test(formData.subdomain)) newErrors.subdomain = 'Subdomain can only contain lowercase letters, numbers, and hyphens';
        if (!formData.email) newErrors.email = 'Email is required';
        if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
        if (!formData.password) newErrors.password = 'Password is required';
        if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
        if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
        if (!formData.firstName) newErrors.firstName = 'First name is required';
        if (!formData.lastName) newErrors.lastName = 'Last name is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setLoading(true);

        try {
            const response = await fetch('/api/company/register', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();

            if (response.ok) {
                setSuccess(true);
            } else {
                setErrors({ general: data.message || 'Registration failed' });
            }
        } catch (error) {
            setErrors({ general: 'An error occurred. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="company-registration">
                <div className="registration-container">
                    <div className="registration-success">
                        <div className="success-icon">&#10003;</div>
                        <h2>Company Registered</h2>
                        <p className="success-company-name">{formData.companyName}</p>
                        <p className="success-message">
                            Your company and administrator account have been created.
                            You can now sign in with <strong>{formData.email}</strong> and the password you chose.
                        </p>
                        <button
                            className="btn-register"
                            onClick={() => history.push('/login')}
                        >
                            Go to Login
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="company-registration">
            <div className="registration-container">
                <div className="registration-header">
                    <h1>ConfigSelect</h1>
                    <p className="registration-tagline">Product Configurator Platform</p>
                </div>

                <h2>Register Your Company</h2>
                <p className="subtitle">Create your company account and administrator login</p>

                {errors.general && (
                    <div className="error-banner">{errors.general}</div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="form-section">
                        <h3>Company Information</h3>

                        <div className="form-group">
                            <label>Company Name *</label>
                            <input
                                type="text"
                                name="companyName"
                                value={formData.companyName}
                                onChange={handleChange}
                                placeholder="Your company name"
                                className={errors.companyName ? 'error' : ''}
                            />
                            {errors.companyName && <span className="error-text">{errors.companyName}</span>}
                        </div>

                        <div className="form-group">
                            <label>Subdomain *</label>
                            <div className="subdomain-input">
                                <input
                                    type="text"
                                    name="subdomain"
                                    value={formData.subdomain}
                                    onChange={handleChange}
                                    className={errors.subdomain ? 'error' : ''}
                                />
                                <span className="subdomain-suffix">.configselect.com</span>
                            </div>
                            {errors.subdomain && <span className="error-text">{errors.subdomain}</span>}
                            <span className="help-text">This will be your unique portal URL</span>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>Company Administrator</h3>
                        <p className="section-note">This person will manage your company's users, products, and settings.</p>

                        <div className="form-row">
                            <div className="form-group">
                                <label>First Name *</label>
                                <input
                                    type="text"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    placeholder="First name"
                                    className={errors.firstName ? 'error' : ''}
                                />
                                {errors.firstName && <span className="error-text">{errors.firstName}</span>}
                            </div>

                            <div className="form-group">
                                <label>Last Name *</label>
                                <input
                                    type="text"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    placeholder="Last name"
                                    className={errors.lastName ? 'error' : ''}
                                />
                                {errors.lastName && <span className="error-text">{errors.lastName}</span>}
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Email *</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="admin@company.com"
                                className={errors.email ? 'error' : ''}
                            />
                            {errors.email && <span className="error-text">{errors.email}</span>}
                        </div>

                        <div className="form-group">
                            <label>Phone</label>
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="Phone number"
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Password *</label>
                                <input
                                    type="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder="Minimum 8 characters"
                                    className={errors.password ? 'error' : ''}
                                />
                                {errors.password && <span className="error-text">{errors.password}</span>}
                            </div>

                            <div className="form-group">
                                <label>Confirm Password *</label>
                                <input
                                    type="password"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    placeholder="Confirm password"
                                    className={errors.confirmPassword ? 'error' : ''}
                                />
                                {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
                            </div>
                        </div>
                    </div>

                    <button type="submit" className="btn-register" disabled={loading}>
                        {loading ? 'Creating Company...' : 'Register Company'}
                    </button>
                </form>

                <p className="login-link">
                    Already have an account? <a href="/login">Sign in</a>
                </p>
            </div>
        </div>
    );
};

export default CompanyRegistration;
